import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DuplicatePreference, ProcessingStatus, Processors } from '../../types/generic';
import { RootState } from '..';

export interface ProcessingState {
	fileName: string;
	fileId: string;
	status: ProcessingStatus;
	operation: string;
	processor?: Processors;
	duplicatePreference?: DuplicatePreference;
	duplicates: number;
	duplicateBatches?: string[];
	notifyOnComplete?: boolean;
	enrichments: string[];
}

const initialState: ProcessingState = {
	fileName: '',
	fileId: '',
	status: ProcessingStatus.IN_PROGRESS,
	operation: '',
	duplicates: 0,
	enrichments: [],
};

const processingSlice = createSlice({
	name: 'processing',
	initialState,
	reducers: {
		beginProcessing: (
			state,
			action: PayloadAction<{
				fileName: string;
				fileId: string;
				operation: string;
				processor: Processors;
				duplicatePreference: DuplicatePreference;
				enrichments: string[];
			}>
		) => {
			return {
				...state,
				fileName: action.payload.fileName,
				fileId: action.payload.fileId,
				status: ProcessingStatus.IN_PROGRESS,
				operation: action.payload.operation,
				processor: action.payload.processor,
				duplicatePreference: action.payload.duplicatePreference,
				duplicateBatches: [],
				enrichments: action.payload.enrichments,
			};
		},
		//TODO: logic to communicate with the backend
		cancelProcessing: () => {
			return {
				...initialState,
				processor: undefined,
			};
		},
		finishProcessing: (state, action: PayloadAction<boolean>) => {
			return {
				...state,
				status: action.payload ? ProcessingStatus.FINISHED : ProcessingStatus.ERRORED,
			};
		},
		incrementDuplicates: (state, action: PayloadAction<{ duplicates: number; newBatch: string }>) => {
			return {
				...state,
				duplicates: state.duplicates + action.payload.duplicates,
				duplicateBatches: [...(state.duplicateBatches as string[]), action.payload.newBatch],
			};
		},
		updateNotifyPref: (state, action: PayloadAction<boolean>) => {
			return {
				...state,
				notifyOnComplete: action.payload,
			};
		},
	},
});

export const getDuplicates = (state: RootState): number => state.processing.duplicates;

export const getProcessingComplete = createSelector([(state) => state.processing.status], (processingStatus) => {
	return processingStatus !== ProcessingStatus.IN_PROGRESS;
});

export const { beginProcessing, cancelProcessing, finishProcessing, incrementDuplicates, updateNotifyPref } = processingSlice.actions;
export default processingSlice.reducer;
